import React, { useState, useEffect } from 'react';
import { X, Send, MessageCircle, Edit3, CheckCircle, ArrowRight, Smartphone } from 'lucide-react';

const WhatsAppPreviewModal = ({ 
  isOpen, 
  onClose, 
  venueName, 
  phoneNumber, 
  actionType, // 'quick_quote' | 'availability' | 'info'
  onSend,
  customMessage = null,
  hideQuickInfo = false // Sahipsiz mekanlar için hızlı bilgi ibarelerini gizle
}) => {
  // Aksiyon tipine göre varsayılan mesajları belirle
  const getDefaultMessage = () => {
    switch (actionType) {
      case 'quick_quote':
        return `Merhaba,

davetevibulcom üzerinden ${venueName} isimli mekanınızı inceledim.

*Fiyat teklifi almak istiyorum*

Etkinlik detaylarımı paylaşmak istiyorum. Uygun olduğunuzda bana dönüş yapabilir misiniz?

Teşekkürler.`;
      case 'availability':
        return `Merhaba,

davetevibulcom üzerinden ${venueName} isimli mekanınızı inceledim.

*Müsaitlik durumunu öğrenmek istiyorum*

Planladığım tarihte mekanınızın uygun olup olmadığını öğrenebilir miyim?

Teşekkürler.`;
      case 'info':
        return `Merhaba,

davetevibulcom üzerinden ${venueName} isimli mekanınızı inceledim.

*Mekanınız hakkında bilgi almak istiyorum*

Fiyat, kapasite ve hizmetleriniz hakkında bilgi alabilir miyim?

Teşekkürler.`;
      default:
        return customMessage || `Merhaba, ${venueName} hakkında bilgi almak istiyorum.`;
    }
  };


  const getActionTitle = () => {
    switch (actionType) {
      case 'quick_quote':
        return 'Teklif Al';
      case 'availability':
        return 'Müsaitlik Öğren';
      case 'info':
        return 'Bilgi Al';
      default:
        return 'Mesaj Gönder';
    }
  };

  const [message, setMessage] = useState(getDefaultMessage());
  const [isEditing, setIsEditing] = useState(false);

  // Reset message when modal opens or action type changes
  useEffect(() => {
    if (isOpen) {
      setMessage(customMessage || getDefaultMessage());
      setIsEditing(false);
    }
  }, [isOpen, actionType, venueName]);

  // Format phone number for WhatsApp
  const formatWhatsAppNumber = (number) => {
    if (!number) return '';
    let cleaned = number.replace(/[\s\-\(\)]/g, '');
    if (cleaned.startsWith('+90')) {
      cleaned = cleaned.substring(3);
    } else if (cleaned.startsWith('90')) {
      cleaned = cleaned.substring(2);
    } else if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    return '90' + cleaned;
  };

  const handleSend = () => {
    const formattedPhone = formatWhatsAppNumber(phoneNumber);
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
    
    // Callback for tracking
    if (onSend) {
      onSend(message);
    }
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* WhatsApp Style Header */}
        <div className="bg-gradient-to-r from-[#075E54] to-[#128C7E] px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2.5 rounded-full">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white text-lg">{getActionTitle()}</h3>
                <p className="text-green-100 text-sm">{venueName}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-full transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-5 py-4 border-b border-green-100">
          <div className="flex items-start gap-3">
            <div className="bg-green-100 p-2 rounded-full flex-shrink-0 mt-0.5">
              <Smartphone className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-green-800 text-sm font-medium">
                WhatsApp'a yönlendirileceksiniz
              </p>
              <p className="text-green-600 text-xs mt-1">
                Aşağıdaki mesaj sizin adınıza hazırlandı. Mekanla hızlı iletişim kurabilirsiniz.
              </p>
            </div>
          </div>
        </div>

        {/* Message Preview - WhatsApp Chat Style */}
        <div className="p-5 bg-[#ECE5DD]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}>
          <div className="flex justify-end mb-2">
            <div className="bg-[#DCF8C6] rounded-2xl rounded-tr-sm px-4 py-3 max-w-[90%] shadow-sm">
              {isEditing ? (
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full min-h-[150px] bg-transparent text-gray-800 text-sm resize-none focus:outline-none"
                  autoFocus
                />
              ) : (
                <p className="text-gray-800 text-sm whitespace-pre-line">{message}</p>
              )}
              <div className="flex items-center justify-end gap-1 mt-2">
                <span className="text-[10px] text-gray-500">
                  {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <CheckCircle className="w-3 h-3 text-blue-500" />
              </div>
            </div>
          </div>
          
          {/* Edit Button */}
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-2 text-xs text-gray-600 hover:text-green-700 transition-colors mx-auto mt-2"
          >
            <Edit3 className="w-3.5 h-3.5" />
            {isEditing ? 'Düzenlemeyi Bitir' : 'Mesajı Düzenle'}
          </button>
        </div>

        {/* Action Buttons */}
        <div className="p-5 bg-white border-t border-gray-100">
          {/* Benefits - Sahipsiz mekanlarda gizle */}
          {!hideQuickInfo && (
          <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
              <span>Hızlı yanıt</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
              <span>Güvenli iletişim</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
              <span>Ücretsiz</span>
            </div>
          </div>
          )}

          {/* Send Button */}
          <button
            onClick={handleSend}
            className="w-full bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#22c55e] hover:to-[#0d7a6e] text-white font-semibold py-4 px-6 rounded-2xl flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transition-all duration-200 group"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span>WhatsApp'ta Gönder</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Cancel Link */}
          <button
            onClick={onClose}
            className="w-full text-gray-500 hover:text-gray-700 text-sm mt-3 py-2 transition-colors"
          >
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppPreviewModal;
